import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Session } from "@/lib/types";
import { sessionKey, useSession } from "@/lib/session";
import { CloseAccountPanel } from "@/features/account/CloseAccountPanel";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { SettingsLayout, SettingsSection } from "@/ui/Settings";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { Button } from "@/ui/Button";
import { Field, Input } from "@/ui/Field";
import { Dialog } from "@/ui/Overlay";
import { Chip } from "@/ui/Chip";
import { ErrorState, TableSkeleton } from "@/ui/States";
import { FormError, FormNotice } from "@/features/auth/AuthCard";
import { CopyField } from "@/ui/Copy";
import { QRCode } from "@/ui/QRCode";
import { useToast } from "@/ui/Toast";
import { absoluteTime, describeDevice, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/account/security")({
  component: SecurityScreen,
});

function SecurityScreen() {
  return (
    <div className="flex flex-col gap-8">
      <Head
        title="Security"
        description="Your password, second factor, and the devices that are signed in."
      />

      {/* Four separate subjects, which is what makes this a settings screen
          rather than a form. Stacked in one column they were a scroll with
          no table of contents and no way back up. */}
      <SettingsLayout
        sections={[
          { id: "password", label: "Password" },
          { id: "two-factor", label: "Two-factor" },
          { id: "devices", label: "Signed-in devices" },
          { id: "close", label: "Close account" },
        ]}
      >
        <SettingsSection
          id="password"
          title="Password"
          description="Changing your password signs you out everywhere else."
        >
          <PasswordPanel />
        </SettingsSection>

        <SettingsSection
          id="two-factor"
          title="Two-factor authentication"
          description="A code from an authenticator app, in addition to your password."
        >
          <TwoFactorPanel />
        </SettingsSection>

        <SettingsSection
          id="devices"
          title="Signed-in devices"
          description="Every browser currently holding a session for your account."
          wide
        >
          <SessionsPanel />
        </SettingsSection>

        <SettingsSection
          id="close"
          title="Close this account"
          description="Deletes your account, and every organisation nobody else owns along with it."
        >
          <CloseAccountPanel />
        </SettingsSection>
      </SettingsLayout>
    </div>
  );
}

const MIN_PASSWORD_LENGTH = 12;

function PasswordPanel() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");

  const change = useMutation({
    mutationFn: () => api.post<void>("/me/password", { current_password: current, new_password: next }),
    onSuccess: () => {
      toast.success("Password changed", "You have been signed out on every other device.");
      setCurrent("");
      setNext("");
    },
  });

  return (
    <Panel
      footer={
        <>
          <span className="text-[13px] text-[var(--text-muted)]">
            At least {MIN_PASSWORD_LENGTH} characters.
          </span>
          <Button
            tone="primary"
            size="dense"
            busy={change.isPending}
            disabled={!current || next.length < MIN_PASSWORD_LENGTH}
            onClick={() => change.mutate()}
          >
            Change password
          </Button>
        </>
      }
    >
      <form
        className="flex max-w-sm flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          change.mutate();
        }}
      >
        <Field label="Current password" required>
          {({ id }) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
        </Field>
        <Field label="New password" required>
          {({ id }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>
        {change.error ? <FormError error={change.error} /> : null}
      </form>
    </Panel>
  );
}

function TwoFactorPanel() {
  const { user } = useSession();
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const remaining = useQuery({
    queryKey: ["mfa-recovery-codes"],
    queryFn: ({ signal }) =>
      api.get<{ remaining: number }>("/me/mfa/recovery-codes", undefined, signal),
    enabled: Boolean(user?.mfa_enabled),
  });

  const enabled = Boolean(user?.mfa_enabled);

  return (
    <>
      <EnrolDialog open={enrolling} onOpenChange={setEnrolling} />
      <DisableDialog open={disabling} onOpenChange={setDisabling} />

      <Panel>
        <div className="flex flex-col gap-3">
          {/* The switch sits on the line that says what it will change.
              In the card's actions slot it was drawn above the status, on a
              row of its own — a button to turn something off, printed before
              the sentence saying whether it was on. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5">
            <Chip tone={enabled ? "live" : "warn"}>{enabled ? "On" : "Off"}</Chip>
            <span className="min-w-0 flex-1 text-[13px] text-[var(--text-secondary)]">
              {enabled
                ? "You will be asked for a code each time you sign in."
                : "Your account is protected by your password alone."}
            </span>
            {enabled ? (
              <Button size="dense" tone="secondary" onClick={() => setDisabling(true)}>
                Turn off
              </Button>
            ) : (
              <Button size="dense" tone="primary" onClick={() => setEnrolling(true)}>
                Turn on
              </Button>
            )}
          </div>

          {enabled && remaining.data && (
            <p className="text-[13px] text-[var(--text-secondary)]">
              {remaining.data.remaining} recovery code
              {remaining.data.remaining === 1 ? "" : "s"} remaining.
              {remaining.data.remaining <= 3 && (
                <span className="ml-1 text-[var(--warning)]">
                  Generate a new set soon.
                </span>
              )}
            </p>
          )}
        </div>
      </Panel>
    </>
  );
}

function EnrolDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const begin = useQuery({
    queryKey: ["mfa-enrolment"],
    queryFn: ({ signal }) =>
      api.post<{ secret: string; provisioning_uri: string }>("/me/mfa", undefined, { signal }),
    enabled: open && recoveryCodes === null,
    gcTime: 0,
    retry: false,
  });

  const confirm = useMutation({
    mutationFn: () => api.post<{ codes: string[] }>("/me/mfa/confirm", { code }),
    onSuccess: async (result) => {
      setRecoveryCodes(result.codes);
      await queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  });

  function close() {
    onOpenChange(false);
    window.setTimeout(() => {
      setRecoveryCodes(null);
      setCode("");
      confirm.reset();
    }, 200);
  }

  if (recoveryCodes) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => !next && close()}
        title="Save your recovery codes"
        description="Each code works once. They are the only way back in if you lose your device."
        footer={
          <Button tone="primary" onClick={close}>
            I have saved them
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <CopyField label="Recovery codes" value={recoveryCodes.join("\n")} multiline />
          <FormNotice title="Two-factor authentication is on.">
            You will be asked for a code the next time you sign in.
          </FormNotice>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Set up two-factor authentication"
      description="Scan the code with an authenticator app, then enter the six digits it shows."
      footer={
        <>
          <Button tone="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            tone="primary"
            busy={confirm.isPending}
            disabled={code.length !== 6}
            onClick={() => confirm.mutate()}
          >
            Turn on
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {begin.isError && <ErrorState error={begin.error} compact />}

        {begin.data && (
          <>
            <div className="flex justify-center rounded-lg border border-[var(--border)] bg-white p-4">
              <QRCode value={begin.data.provisioning_uri} size={168} />
            </div>
            <details className="text-[13px]">
              <summary className="cursor-pointer text-[var(--text-secondary)]">
                Cannot scan the code?
              </summary>
              <div className="mt-2">
                <CopyField label="Setup key" value={begin.data.secret} />
              </div>
            </details>
          </>
        )}

        <Field label="Six-digit code" required>
          {({ id }) => (
            <Input
              id={id}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              className="mono tracking-[0.3em]"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          )}
        </Field>

        {confirm.error ? <FormError error={confirm.error} /> : null}
      </div>
    </Dialog>
  );
}

function DisableDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [password, setPassword] = useState("");

  const disable = useMutation({
    mutationFn: () => api.delete<void>("/me/mfa", { password }),
    onSuccess: async () => {
      toast.success("Two-factor authentication turned off");
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      onOpenChange(false);
      setPassword("");
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Turn off two-factor authentication"
      description="Your account will be protected by your password alone."
      size="sm"
      footer={
        <>
          <Button tone="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button tone="danger" busy={disable.isPending} disabled={!password} onClick={() => disable.mutate()}>
            Turn off
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Your password" required hint="Required so that a stolen session cannot remove your second factor.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>
        {disable.error ? <FormError error={disable.error} /> : null}
      </div>
    </Dialog>
  );
}

function SessionsPanel() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const sessions = useQuery({
    queryKey: ["sessions"],
    queryFn: ({ signal }) => api.get<{ sessions: Session[] }>("/me/sessions", undefined, signal),
  });

  const revoke = useMutation({
    mutationFn: (sessionId: string) => api.delete<void>(`/me/sessions/${sessionId}`),
    onSuccess: async () => {
      toast.success("Device signed out");
      await queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
    onError: toast.error,
  });

  const revokeAll = useMutation({
    mutationFn: () => api.post<{ revoked_sessions: number }>("/me/sessions/revoke-all"),
    onSuccess: async (result) => {
      toast.success(
        `Signed out ${result.revoked_sessions} device${result.revoked_sessions === 1 ? "" : "s"}`,
      );
      await queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
    onError: toast.error,
  });

  const others = sessions.data?.sessions.filter((session) => !session.current).length ?? 0;

  return (
    <Panel
      padded={false}
      actions={
        others > 0 ? (
          <Button size="dense" tone="secondary" busy={revokeAll.isPending} onClick={() => revokeAll.mutate()}>
            Sign out everywhere else
          </Button>
        ) : undefined
      }
    >
      {sessions.isLoading && <TableSkeleton rows={2} columns={4} />}
      {sessions.isError && (
        <div className="px-5 py-4">
          <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
        </div>
      )}

      {sessions.data && (
        <div className="px-5 py-4">
          <Table>
            <thead>
              <tr>
                <Th>Device</Th>
                <Th>Address</Th>
                <Th>Last active</Th>
                <Th align="right" quiet>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {sessions.data.sessions.map((session) => (
                <Tr key={session.id}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{describeDevice(session.user_agent)}</span>
                      {session.current && <Chip tone="live">This device</Chip>}
                      {!session.remembered && <Chip>Until browser closes</Chip>}
                    </div>
                    <span className="text-[11px] text-[var(--text-muted)]">
                      Signed in {absoluteTime(session.created_at)} · ends{" "}
                      <span title={absoluteTime(session.expires_at)}>
                        {relativeTime(session.expires_at)}
                      </span>
                    </span>
                  </Td>
                  <Td className="mono text-[13px] text-[var(--text-secondary)]">
                    {session.ip_address || "—"}
                  </Td>
                  <Td className="text-[var(--text-secondary)]">{relativeTime(session.last_seen_at)}</Td>
                  <Td actions>
                    {!session.current && (
                      <Button
                        size="dense"
                        tone="ghost"
                        busy={revoke.isPending && revoke.variables === session.id}
                        onClick={() => revoke.mutate(session.id)}
                      >
                        Sign out
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Panel>
  );
}

